import {
  AddressWithSettings,
  GUIDEBOOK_V2_CATEGORIES,
  GuidebookV2CategoryId,
  isCustomSectionKey,
  isPredefinedV2Key,
  PAID_PARKING_FIELD_KEY,
  SECTION_ORDER_KEY,
  slugifyCustomLabel,
  UpsertGuidebookEntry,
} from './address-guidebook.service';
import { guidebookAddressField, guidebookPhotoType } from './guidebook-address-fields';

import { parkingOptionLabel } from 'src/shared/constants/parking-options';

/**
 * Pure view-model layer for the guidebook detail page. The guidebook is one flat
 * address_settings bucket; every row is projected into a GuidebookEntry that belongs
 * to exactly one audience. `visible_to_guest` is the single source of truth for the
 * split — "guest" entries render on the public guidebook site (the external guidebook),
 * "internal" entries are notes only the owner, their team and TIDY see.
 */

export type GuidebookAudience = 'guest' | 'internal';

/**
 * top        — welcome / check-in / house rules: fixed slots with their own visibility flag
 * predefined — the v2 category fields (door code, trash day, …): fixed slots, always guest
 * address    — fields that live on the property record (wifi, home_access, parking)
 * custom     — free-form sections the owner (or concierge) added
 */
export type GuidebookEntryKind = 'top' | 'predefined' | 'address' | 'custom';

export interface GuidebookEntry {
  key: string;
  /** Label exactly as stored; shown verbatim wherever the entry is rendered. */
  label: string;
  value: string;
  audience: GuidebookAudience;
  kind: GuidebookEntryKind;
  /** Guest-website section this entry renders under; null = legacy free-form section. */
  categoryId: GuidebookV2CategoryId | null;
  inputType: 'text' | 'textarea' | 'select';
  /** Whether the entry can switch audience. Predefined and property-record fields cannot. */
  movable: boolean;
  /** Whether the entry can be removed outright. Fixed slots can only be cleared. */
  deletable: boolean;
  addressField?: string;
  /** Property-record value as stored; `value` holds its readable label for select fields. */
  rawValue?: unknown;
}

export interface GuidebookPhoto {
  id: number;
  url: string;
  caption: string;
}

export interface GuidebookModel {
  propertyName: string;
  /** Single-line street address, formatted the way the public site shows it. */
  propertyAddress: string;
  entries: GuidebookEntry[];
  categoryOrder: GuidebookV2CategoryId[];
  categoryVisibility: Record<GuidebookV2CategoryId, boolean>;
  /** Edit Property photos for the fixed cards. */
  photos: Partial<Record<GuidebookV2CategoryId, GuidebookPhoto[]>>;
  contactCollectionEnabled: boolean;
  identityVerificationEnabled: boolean;
}

/** What the section editor dialog is opened for. */
export type GuidebookEditorTarget =
  | { type: 'text'; key: string }
  | { type: 'category'; categoryId: GuidebookV2CategoryId }
  | { type: 'custom'; key: string }
  | { type: 'new' };

export interface GuidebookSectionEditorParams {
  addressId: number;
  audience: GuidebookAudience;
  target: GuidebookEditorTarget;
}

export const TOP_LEVEL_FIELDS: { key: string; label: string }[] = [
  { key: 'welcome_message', label: 'Welcome message' },
  { key: 'check_in_instructions', label: 'Check-in instructions' },
  { key: 'house_rules', label: 'House rules' },
];

export const CATEGORY_VISIBILITY_PREFIX = 'category_visible_';
const CONTACT_COLLECTION_KEY = 'contact_collection_enabled';
const IDENTITY_VERIFICATION_KEY = 'identity_verification_enabled';

const CATEGORY_IDS = new Set<string>(GUIDEBOOK_V2_CATEGORIES.map(category => category.id));
const FIXED_CATEGORY_IDS = new Set<string>(GUIDEBOOK_V2_CATEGORIES.filter(category => category.fixed).map(category => category.id));

// Slots the old Access card had. Rows saved there stay readable as their own sections,
// but the empty ones the old editor wrote for every slot are dropped.
export const RETIRED_ACCESS_KEYS: ReadonlySet<string> = new Set([
  'custom_door_code',
  'custom_lockbox_location',
  'custom_gate_code',
  'custom_alarm_code',
  'custom_backup_entry',
]);

interface PhotoNoteLike {
  id: number;
  type: string;
  photo_url: string;
  photo_note_caption?: string;
  caption?: string;
}

export function isFixedCategory(categoryId: string | null): boolean {
  return !!categoryId && FIXED_CATEGORY_IDS.has(categoryId);
}

export function normalizeCategoryOrder(stored: unknown[]): GuidebookV2CategoryId[] {
  const ordered: GuidebookV2CategoryId[] = [];
  for (const id of stored) {
    if (typeof id !== 'string' || !CATEGORY_IDS.has(id) || ordered.includes(id as GuidebookV2CategoryId)) { continue; }
    ordered.push(id as GuidebookV2CategoryId);
  }
  // A category added after the order was saved slots in ahead of the one it precedes by
  // default, so How to Park lands right before How to Get In.
  GUIDEBOOK_V2_CATEGORIES.forEach((category, index) => {
    if (ordered.includes(category.id)) { return; }
    const next = GUIDEBOOK_V2_CATEGORIES[index + 1];
    const at = next ? ordered.indexOf(next.id) : -1;
    if (at === -1) {
      ordered.push(category.id);
    } else {
      ordered.splice(at, 0, category.id);
    }
  });
  return ordered;
}

function deriveLabelFromKey(key: string): string {
  const raw = key.startsWith('custom_') ? key.slice('custom_'.length) : key;
  return raw.replace(/_+/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

/**
 * Rental accounts see the guest_* parking / access columns; everyone else the pro ones
 * (see guidebookAddressField).
 */
export function buildGuidebookModel(address: AddressWithSettings, isRental = false): GuidebookModel {
  const byKey: Record<string, Record<string, unknown>> = {};
  const guidebookSettings = (address.address_settings || []).filter(setting => {
    // The store is shared with listing / optimizer rows; only guidebook rows are content here.
    const category = (setting as { category?: string }).category;
    return !category || category === 'guidebook';
  });
  for (const setting of guidebookSettings) {
    byKey[setting.key] = setting.data || {};
  }
  const readString = (key: string) => {
    const value = byKey[key]?.['value'];
    return typeof value === 'string' ? value : '';
  };
  const readBool = (key: string, fallback: boolean) => {
    const value = byKey[key]?.['value'];
    return typeof value === 'boolean' ? value : fallback;
  };
  const readVisible = (key: string) => byKey[key]?.['visible_to_guest'] !== false;

  const entries: GuidebookEntry[] = [];

  for (const field of TOP_LEVEL_FIELDS) {
    const audience: GuidebookAudience = readVisible(field.key) ? 'guest' : 'internal';
    entries.push({
      key: field.key,
      label: field.label,
      value: readString(field.key),
      audience,
      kind: 'top',
      categoryId: null,
      inputType: 'textarea',
      movable: true,
      deletable: false,
    });
  }

  const categoryVisibility = {} as Record<GuidebookV2CategoryId, boolean>;
  const photos: Partial<Record<GuidebookV2CategoryId, GuidebookPhoto[]>> = {};
  const photoNotes = Array.isArray(address['photo_notes']) ? address['photo_notes'] as PhotoNoteLike[] : [];
  for (const category of GUIDEBOOK_V2_CATEGORIES) {
    // Data saved before category_visible_* existed mirrored the flag onto each field.
    const firstPredefined = category.fields.find(field => !field.fromAddressModel);
    const fallback = firstPredefined ? readVisible(firstPredefined.key) : true;
    // Fixed cards can't be hidden, so an old hidden flag on Access no longer applies.
    categoryVisibility[category.id] = category.fixed || readBool(`${CATEGORY_VISIBILITY_PREFIX}${category.id}`, fallback);

    if (category.photoKind) {
      const type = guidebookPhotoType(category.photoKind, isRental);
      photos[category.id] = photoNotes
        .filter(note => note?.type === type && !!note.photo_url)
        .map(note => ({ id: note.id, url: note.photo_url, caption: note.photo_note_caption || note.caption || '' }));
    }

    for (const field of category.fields) {
      if (field.fromAddressModel && field.addressField) {
        const column = guidebookAddressField(field.addressField, isRental);
        const raw = address[column];
        const value = field.inputType === 'select'
          ? parkingOptionLabel([...(field.options || []), ...(field.optionsWhenPaid || [])], raw)
          : typeof raw === 'string' ? raw : '';
        entries.push({
          key: field.key,
          label: field.label,
          value,
          audience: 'guest',
          kind: 'address',
          categoryId: category.id,
          inputType: field.inputType || 'text',
          movable: false,
          deletable: false,
          addressField: column,
          rawValue: raw ?? null,
        });
      } else {
        entries.push({
          key: field.key,
          label: field.label,
          value: readString(field.key),
          audience: 'guest',
          kind: 'predefined',
          categoryId: category.id,
          inputType: field.inputType || 'text',
          movable: false,
          deletable: false,
        });
      }
    }
  }

  for (const setting of guidebookSettings) {
    if (!isCustomSectionKey(setting.key) || isPredefinedV2Key(setting.key)) { continue; }
    const data = setting.data || {};
    if (data['deleted'] === true) { continue; }
    const label = typeof data['label'] === 'string' ? data['label'] : deriveLabelFromKey(setting.key);
    const value = typeof data['value'] === 'string' ? data['value'] : '';
    if (RETIRED_ACCESS_KEYS.has(setting.key) && value.trim() === '') { continue; }
    const audience: GuidebookAudience = data['visible_to_guest'] === false ? 'internal' : 'guest';
    const rawCategory = data['category'];
    // Fixed cards only hold Edit Property fields; rows filed under one become their own section.
    const categoryId = typeof rawCategory === 'string' && CATEGORY_IDS.has(rawCategory) && !isFixedCategory(rawCategory)
      ? rawCategory as GuidebookV2CategoryId
      : null;
    entries.push({
      key: setting.key,
      label,
      value,
      audience,
      kind: 'custom',
      categoryId,
      inputType: 'textarea',
      movable: true,
      deletable: true,
    });
  }

  const storedOrder = byKey[SECTION_ORDER_KEY]?.['value'];

  return {
    propertyName: address.address_name || address.address1 || 'Property',
    propertyAddress: [address.address1, address.city, address.state, address.zip]
      .filter(part => typeof part === 'string' && part.trim() !== '')
      .join(', '),
    entries,
    categoryOrder: normalizeCategoryOrder(Array.isArray(storedOrder) ? storedOrder : []),
    categoryVisibility,
    photos,
    // The guest guidebook shows the contact form unless this is explicitly false.
    contactCollectionEnabled: readBool(CONTACT_COLLECTION_KEY, true),
    identityVerificationEnabled: readBool(IDENTITY_VERIFICATION_KEY, false),
  };
}

/**
 * Filled property-record entries a fixed card shows, in field order. Payment details
 * only count while the parking is paid.
 */
export function fixedCardEntries(categoryId: GuidebookV2CategoryId, entries: GuidebookEntry[]): GuidebookEntry[] {
  const category = GUIDEBOOK_V2_CATEGORIES.find(candidate => candidate.id === categoryId);
  if (!category?.fixed) { return []; }
  const isPaid = entries.find(entry => entry.key === PAID_PARKING_FIELD_KEY)?.rawValue === true;
  return category.fields
    .filter(field => !field.onlyWhenPaid || isPaid)
    .map(field => entries.find(entry => entry.key === field.key && entry.kind === 'address'))
    .filter((entry): entry is GuidebookEntry => !!entry && entry.value.trim() !== '');
}

/** Returns a copy of the entry on the other side of the split. */
export function withAudience(entry: GuidebookEntry, audience: GuidebookAudience): GuidebookEntry {
  return { ...entry, audience };
}

/** Applies an edit. Fixed slots keep their label; custom entries take the label as typed. */
export function withText(entry: GuidebookEntry, label: string, value: string): GuidebookEntry {
  if (entry.kind !== 'custom') {
    return { ...entry, value };
  }
  return { ...entry, label: (label || '').trim(), value };
}

export interface NewEntryInput {
  label: string;
  value: string;
  audience: GuidebookAudience;
  categoryId: GuidebookV2CategoryId | null;
}

export function createCustomEntry(input: NewEntryInput, existingKeys: Set<string>): GuidebookEntry {
  const label = (input.label || '').trim();
  let key = slugifyCustomLabel(label || `section_${Date.now()}`);
  while (existingKeys.has(key)) {
    key = `${key}_${Math.floor(Math.random() * 1000)}`;
  }
  return {
    key,
    label,
    value: input.value || '',
    audience: input.audience,
    kind: 'custom',
    categoryId: input.categoryId,
    inputType: 'textarea',
    movable: true,
    deletable: true,
  };
}

/** Upsert row for a setting-backed entry. Property-record fields save through the address API instead. */
export function entryUpsert(
  entry: GuidebookEntry,
  categoryVisibility: Record<GuidebookV2CategoryId, boolean>
): UpsertGuidebookEntry | null {
  switch (entry.kind) {
    case 'address':
      return null;
    case 'top':
      return { key: entry.key, data: { value: entry.value, visible_to_guest: entry.audience === 'guest' } };
    case 'predefined':
      // Predefined fields mirror their section's visibility so the renderer's pre-category fallback still works.
      return {
        key: entry.key,
        data: {
          label: entry.label,
          value: entry.value,
          visible_to_guest: entry.categoryId ? categoryVisibility[entry.categoryId] !== false : true,
          category: entry.categoryId,
        },
      };
    case 'custom': {
      const data: Record<string, unknown> = {
        label: entry.label,
        value: entry.value,
        visible_to_guest: entry.audience === 'guest',
      };
      if (entry.categoryId) { data['category'] = entry.categoryId; }
      return { key: entry.key, data };
    }
  }
}

export function categoryVisibilityUpserts(
  categoryId: GuidebookV2CategoryId,
  visible: boolean,
  entries: GuidebookEntry[]
): UpsertGuidebookEntry[] {
  const rows: UpsertGuidebookEntry[] = [
    { key: `${CATEGORY_VISIBILITY_PREFIX}${categoryId}`, data: { value: visible } },
  ];
  for (const entry of entries) {
    if (entry.kind !== 'predefined' || entry.categoryId !== categoryId) { continue; }
    rows.push({
      key: entry.key,
      data: { label: entry.label, value: entry.value, visible_to_guest: visible, category: categoryId },
    });
  }
  return rows;
}

export function sectionOrderUpsert(order: GuidebookV2CategoryId[]): UpsertGuidebookEntry {
  return { key: SECTION_ORDER_KEY, data: { value: order } };
}

export function guestSettingsUpserts(contactCollection: boolean, identityVerification: boolean): UpsertGuidebookEntry[] {
  return [
    { key: CONTACT_COLLECTION_KEY, data: { value: contactCollection } },
    { key: IDENTITY_VERIFICATION_KEY, data: { value: identityVerification } },
  ];
}

export function matchesQuery(entry: GuidebookEntry, normalizedQuery: string): boolean {
  if (!normalizedQuery) { return true; }
  return entry.label.toLowerCase().includes(normalizedQuery)
    || entry.value.toLowerCase().includes(normalizedQuery);
}
